import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "@/i18n";
import { fileUrl, isVideo } from "@/api";
import { scrollToId } from "@/pages/Landing";

const MaskedLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ settings, heroImage }) => {
  const { t, pick } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const words = (settings.studio_name || "TATTOO STUDIO").split(" ");

  return (
    <section id="top" ref={ref} className="relative h-[100svh] overflow-hidden flex items-end" data-testid="hero-section">
      {(() => {
        const bg = settings.hero_video || settings.hero_image || heroImage;
        if (!bg) return null;
        return (
          <motion.div className="absolute inset-0" style={{ y: bgY }}>
            {isVideo(bg) ? (
              <video src={fileUrl(bg)} autoPlay muted loop playsInline className="w-full h-[120%] object-cover opacity-40" />
            ) : (
              <img src={fileUrl(bg, 1600)} alt="" decoding="async" className="w-full h-[120%] object-cover img-bw opacity-40" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/50 to-[#0A0A0B]/30" />
          </motion.div>
        );
      })()}
      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-4 sm:px-8 lg:px-16 pb-20 sm:pb-24">
        <MaskedLine delay={0.2}>
          <span className="font-mono2 text-[11px] sm:text-xs tracking-[0.35em] uppercase text-zinc-400" data-testid="hero-city-line">
            {settings.city_line || "TATTOO STUDIO"}
          </span>
        </MaskedLine>
        <h1 className="font-display uppercase leading-[0.85] tracking-wide mt-4" data-testid="hero-title">
          {words.map((w, i) => (
            <MaskedLine key={i} delay={0.35 + i * 0.12}>
              <span className="text-[18vw] sm:text-[14vw] lg:text-[11vw]">{w}</span>
            </MaskedLine>
          ))}
        </h1>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
          <MaskedLine delay={0.9}>
            <p className="text-base sm:text-lg text-zinc-300 max-w-md" data-testid="hero-tagline">{pick(settings.tagline)}</p>
          </MaskedLine>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="flex gap-3">
            <button onClick={() => scrollToId("#booking")} data-testid="hero-cta-book"
              className="bg-white text-black font-mono2 text-[11px] tracking-[0.25em] uppercase px-7 py-4 hover:bg-[#E63946] hover:text-white transition-colors duration-300">
              {t("hero_cta_book")}
            </button>
            <button onClick={() => scrollToId("#portfolio")} data-testid="hero-cta-portfolio"
              className="border border-white/25 font-mono2 text-[11px] tracking-[0.25em] uppercase px-7 py-4 hover:border-white hover:bg-white/5 transition-colors duration-300">
              {t("hero_cta_portfolio")}
            </button>
          </motion.div>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
        className="absolute bottom-6 right-4 sm:right-8 lg:right-16 z-10 flex items-center gap-2 text-zinc-500">
        <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase">{t("hero_scroll")}</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
